import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, Loader2, Paperclip, X } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { fmtSize, useLabels } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function NewCase() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const L = useLabels();
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [caseType, setCaseType] = useState("complaint");
  const [company, setCompany] = useState("b2b");
  const [priority, setPriority] = useState("medium");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const addFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...chosen].slice(0, 10));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/cases", {
        title: title.trim(),
        description: description.trim(),
        case_type: caseType,
        company,
        priority,
      });
      const caseId = r.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/cases/${caseId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(t("newCase.success", { number: r.data.case_number }));
      navigate(`/cases/${caseId}`);
    } catch (err) {
      toast.error(apiError(err, t("newCase.fail")));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="new-case-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("newCase.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("newCase.subtitle")}</p>
      </div>

      <form onSubmit={submit}>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">{t("newCase.card")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="case-title">{t("newCase.titleLabel")}</Label>
              <Input
                id="case-title"
                required
                minLength={3}
                data-testid="case-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("newCase.titlePh")}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>{t("newCase.type")}</Label>
                <Select value={caseType} onValueChange={setCaseType}>
                  <SelectTrigger data-testid="case-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complaint">{L.type.complaint}</SelectItem>
                    <SelectItem value="case">{L.type.case}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("newCase.company")}</Label>
                <Select value={company} onValueChange={setCompany}>
                  <SelectTrigger data-testid="case-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="b2b">{L.company.b2b}</SelectItem>
                    <SelectItem value="outpatient">{L.company.outpatient}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("newCase.priority")}</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger data-testid="case-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{L.priority.low}</SelectItem>
                    <SelectItem value="medium">{L.priority.medium}</SelectItem>
                    <SelectItem value="high">{L.priority.high}</SelectItem>
                    <SelectItem value="urgent">{L.priority.urgent}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="case-description">{t("newCase.desc")}</Label>
              <Textarea
                id="case-description"
                required
                minLength={5}
                rows={6}
                data-testid="case-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("newCase.descPh")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("newCase.attach")}</Label>
              <div
                data-testid="attachment-dropzone"
                onClick={() => fileRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-delta-limeBorder bg-delta-limeLight/30 px-4 py-8 text-center transition-colors hover:bg-delta-limeLight/60"
              >
                <FileUp className="mb-2 h-6 w-6 text-delta-lime" />
                <p className="text-sm font-medium text-slate-700">{t("newCase.dropTitle")}</p>
                <p className="mt-0.5 text-xs text-slate-400">{t("newCase.dropHint")}</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                data-testid="pdf-upload-input"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx,.xls,.txt,.csv"
                onChange={addFiles}
              />
              {files.length > 0 && (
                <ul className="space-y-1.5" data-testid="attachment-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
                      <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">{f.name}</span>
                      <span className="text-xs text-slate-400">{fmtSize(f.size)}</span>
                      <button
                        type="button"
                        data-testid={`remove-attachment-${i}`}
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" data-testid="cancel-case-button" onClick={() => navigate(-1)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={busy} data-testid="submit-case-button" className="bg-delta-navy hover:bg-delta-navyDark">
                {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {busy ? t("newCase.submitting") : t("newCase.submit")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
